import { CanvasDocument, migrateCoreObject, type CanvasMetaExt } from '@vp/core';
import { exportPng, Scene, type ImageProvider } from '@vp/render';
import { CoreTypes } from '@vp/shared-types';
import type { CanvasSession, SessionHooks } from '../session';
import { isObjectUnavailable } from '../modules';
import { logger, settings, storage } from '../platform';
import { t } from '../i18n';
import { live } from './bridge';
import { AttachmentImages } from './images';
import { ensureFontsFor } from '../fonts';

/**
 * Canvas previews for "My canvases": rendered locally with the PNG export path. A preview remembers
 * when it was made; "My canvases" redraws missing or outdated ones in the background straight from
 * storage (a canvas imported, duplicated, edited in another tab, or closed right after an edit).
 */
const W = 640;
const H = 400;
const pending = new Map<string, { timer: ReturnType<typeof setTimeout>; session: CanvasSession }>();
const listeners = new Set<(id: string) => void>();

/** Notified when a canvas preview was (re)rendered. */
export function onThumbnail(fn: (id: string) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

async function render(id: string, scene: Scene, images: ImageProvider): Promise<void> {
  const key = 'thumb:' + id;
  await ensureFontsFor(scene.doc.allObjects());
  const madeAt = Date.now();
  const bounds = scene.index.totalBounds();
  if (!bounds) {
    await storage.setSetting(key, null);
    await storage.setSetting('thumbAt:' + id, madeAt);
    for (const fn of listeners) fn(id);
    return;
  }
  const pad = Math.max(bounds.w, bounds.h) * 0.06;
  const bw = bounds.w + pad * 2;
  const bh = bounds.h + pad * 2;
  // retina-sharp preview, never upscaled beyond 2x of the real size
  const scale = Math.min(2, 2 * Math.min(W / bw, H / bh));
  const blob = await exportPng(scene, {
    ids: null,
    bounds: { x: bounds.x - pad, y: bounds.y - pad, w: bw, h: bh },
    scale,
    background: settings.background,
    unavailable: 'placeholder',
    isUnavailable: isObjectUnavailable,
    images,
    lod: true,
  });
  await storage.setSetting(key, blob);
  await storage.setSetting('thumbAt:' + id, madeAt);
  for (const fn of listeners) fn(id);
}

export async function makeThumbnail(session: CanvasSession): Promise<void> {
  if (session.isReadOnly) return;
  try {
    await render(session.id, session.scene, live.renderer?.opts.images ?? { get: () => null });
  } catch (e) {
    logger.warn('thumbnail.failed', { canvasId: session.id, error: e instanceof Error ? e.name : 'unknown' });
  }
}

/** Debounced refresh after edits. */
export function scheduleThumbnail(session: CanvasSession, delay = 4000): void {
  clearTimeout(pending.get(session.id)?.timer);
  pending.set(session.id, {
    session,
    timer: setTimeout(() => {
      pending.delete(session.id);
      void makeThumbnail(session);
    }, delay),
  });
}

// leaving the tab (or closing it) right after an edit: draw the preview now instead of in 4 s
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'hidden') return;
    for (const [id, p] of pending) {
      clearTimeout(p.timer);
      pending.delete(id);
      void makeThumbnail(p.session);
    }
  });
}

export async function loadThumbnail(id: string): Promise<string | null> {
  const blob = await storage.getSetting<Blob | null>('thumb:' + id);
  return blob instanceof Blob ? URL.createObjectURL(blob) : null;
}

/** Draw the preview of a canvas that is not open, straight from storage. */
async function renderStored(meta: CanvasMetaExt, hooks: SessionHooks): Promise<void> {
  const { objects, links } = await storage.readCanvasContent(meta.id);
  const doc = new CanvasDocument(meta.id);
  doc.load(objects.map((o) => (o.type.startsWith('core:') ? migrateCoreObject(o) : o)));
  doc.load([], links);
  const scene = new Scene(doc, { unavailableText: t('props.unavailable'), frameLabel: t('props.frameUntitled'), modules: hooks.modules, ...(hooks.diagramCtx?.() ?? {}) });
  // pictures load asynchronously: wait for them (briefly) so the preview shows them
  const srcs = objects.filter((o) => o.type === CoreTypes.image && typeof o.props['attachment'] === 'string').map((o) => 'att:' + (o.props['attachment'] as string));
  let left = srcs.length;
  let done: () => void = () => undefined;
  const ready = new Promise<void>((r) => (done = r));
  const images = new AttachmentImages(() => {
    if (--left <= 0) done();
  });
  for (const s of srcs) images.get(s);
  if (srcs.length) await Promise.race([ready, new Promise((r) => setTimeout(r, 2000))]);
  try {
    await render(meta.id, scene, images);
  } finally {
    scene.dispose();
    images.clear();
  }
}

let running = false;
const queue: CanvasMetaExt[] = [];

/**
 * Redraw missing or outdated previews of canvases that are not open, one at a time in the
 * background. Open canvases keep their own previews current.
 */
export async function ensureThumbnails(canvases: CanvasMetaExt[], hooks: SessionHooks, open: Set<string>): Promise<void> {
  for (const c of canvases) {
    if (open.has(c.id) || queue.some((q) => q.id === c.id)) continue;
    const at = await storage.getSetting<number>('thumbAt:' + c.id);
    const has = (await storage.getSetting<Blob | null>('thumb:' + c.id)) instanceof Blob;
    // camera moves also bump updatedAt; a few seconds of slack keep those from redrawing
    if ((has || (at && !c.objectCount)) && at && at >= c.updatedAt - 5000) continue;
    queue.push(c);
  }
  if (running) return;
  running = true;
  try {
    while (queue.length) {
      const c = queue.shift()!;
      try {
        await renderStored(c, hooks);
      } catch (e) {
        logger.warn('thumbnail.failed', { canvasId: c.id, error: e instanceof Error ? e.name : 'unknown' });
      }
      await new Promise((r) => setTimeout(r, 30));
    }
  } finally {
    running = false;
  }
}
